import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react'
import {
  AdminLoginResult,
  AdminUser,
  AuthState,
  LoginResponse,
} from '@/types'
import { authService } from '@/services/authService'
import { clearNetworkScopedStorage } from '@/utils/privacyPreferences'
import {
  captureAdminSession, clearAdminSession, commitAdminLoginResult, isCurrentAdminSession,
  isAdminTokenValid, readAdminToken, readAdminTokenExpiry, subscribeAdminSession,
  type AdminSessionOwner,
  AdminAuthBusyError,
} from '@/utils/adminAuthSession'

interface AuthContextType extends AuthState {
  login: (email: string, password: string, totp?: string) => Promise<AdminLoginResult>
  completeLogin: (response: LoginResponse) => void
  logout: (reason?: string) => void
  refreshUser: () => Promise<void>
  checkPermission: (permission: string) => boolean
  isTokenRefreshing: boolean
  tokenRefreshError: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { user: AdminUser; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: { user: AdminUser; token: string } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'REFRESH_TOKEN'; payload: string }
  | { type: 'RECOVERY_FAILED'; payload: { token: string | null; error: string } }
  | { type: 'SET_TOKEN_REFRESH_STATE'; payload: { isRefreshing: boolean; error?: string | null } }

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  isTokenRefreshing: false,
  tokenRefreshError: null,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      {
        const loginState = {
          ...state,
          user: action.payload.user,
          token: action.payload.token,
          isAuthenticated: true,
          isLoading: false,
          isTokenRefreshing: false,
          tokenRefreshError: null,
        }
        return loginState
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isTokenRefreshing: false,
        tokenRefreshError: null,
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isTokenRefreshing: false,
        tokenRefreshError: null,
      }
    case 'SET_USER':
      {
        const nextState = {
          ...state,
          user: action.payload.user,
          token: action.payload.token,
          isAuthenticated: true,
          isLoading: false,
          isTokenRefreshing: false,
          tokenRefreshError: null,
        }
        return nextState
      }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload }
    case 'REFRESH_TOKEN':
      return { ...state, token: action.payload }
    case 'RECOVERY_FAILED':
      return {
        ...state,
        user: null,
        token: action.payload.token,
        isAuthenticated: false,
        isLoading: false,
        isTokenRefreshing: false,
        tokenRefreshError: action.payload.error,
      }
    case 'SET_TOKEN_REFRESH_STATE':
      return { 
        ...state, 
        isTokenRefreshing: action.payload.isRefreshing,
        tokenRefreshError: action.payload.error ?? null
      }
    default:
      return state
  }
}

const getErrorStatus = (error: any): number | null =>
  error?.response?.status ?? error?.status ?? null

const isAuthenticationFailure = (error: any): boolean => getErrorStatus(error) === 401

const logAdminLogoutReason = (
  reason: string,
  details?: Record<string, unknown>
): void => {
  const token = localStorage.getItem('admin_portal_token')
  console.warn('[AuthContext] Admin auth state cleared', {
    reason,
    hasToken: Boolean(token),
    hasSessionId: Boolean(localStorage.getItem('admin_portal_session_id')),
    expiresAt: localStorage.getItem('admin_portal_expires_at'),
    tokenExpired: token ? !isAdminTokenValid(token) : null,
    ...details,
  })
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const mountedRef = useRef(true)
  const initializationRef = useRef(0)

  const logout = useCallback((reason = 'explicit-logout', owner = captureAdminSession()) => {
    if (!isCurrentAdminSession(owner)) return
    logAdminLogoutReason(reason)
    clearAdminSession(owner)
    clearNetworkScopedStorage()
    dispatch({ type: 'LOGOUT' })
  }, [])

  const refreshToken = useCallback(async () => {
    const owner = captureAdminSession()
    const token = readAdminToken()
    if (!owner.sessionId) {
      if (token && !isAdminTokenValid(token)) logout('refresh-missing-session', owner)
      return
    }
    if (isAdminTokenValid(token) && readAdminTokenExpiry(token)! - Date.now() >= 300000) return
    dispatch({ type: 'SET_TOKEN_REFRESH_STATE', payload: { isRefreshing: true } })
    try {
      await authService.refreshToken()
      if (!mountedRef.current || !isCurrentAdminSession(owner)) return
      const refreshed = readAdminToken()
      if (!isAdminTokenValid(refreshed)) throw new Error('Refresh returned no valid access token')
      dispatch({ type: 'REFRESH_TOKEN', payload: refreshed })
      dispatch({ type: 'SET_TOKEN_REFRESH_STATE', payload: { isRefreshing: false } })
    } catch (error) {
      if (!mountedRef.current || !isCurrentAdminSession(owner)) return
      if (error instanceof AdminAuthBusyError) {
        dispatch({ type: 'SET_TOKEN_REFRESH_STATE', payload: { isRefreshing: false } })
        return
      }
      if (isAuthenticationFailure(error)) {
        logout('refresh-auth-error', owner)
      } else if (!isAdminTokenValid(readAdminToken())) {
        dispatch({ type: 'RECOVERY_FAILED', payload: { token: null, error: 'Auth refresh failed' } })
      } else {
        dispatch({ type: 'SET_TOKEN_REFRESH_STATE',
          payload: { isRefreshing: false, error: 'Auth refresh failed' } })
      }
      throw error
    }
  }, [logout])

  const completeLogin = useCallback((response: LoginResponse) => {
    if (typeof response.user?.id !== 'string' || !response.user.id) {
      throw new Error('Invalid admin login user')
    }
    commitAdminLoginResult(response)
    initializationRef.current += 1
    dispatch({ type: 'LOGIN_SUCCESS', payload: { user: response.user, token: response.token } })
  }, [])

  const login = async (email: string, password: string, totp?: string): Promise<AdminLoginResult> => {
    dispatch({ type: 'SET_TOKEN_REFRESH_STATE', payload: { isRefreshing: false } })
    const pending = authService.login({ email, password, totp })
    const owner = captureAdminSession()
    try {
      const response = await pending
      if ('mfaSetupRequired' in response) {
        if (!isCurrentAdminSession(owner)) throw new Error('Admin authentication session changed')
        dispatch({ type: 'SET_LOADING', payload: false })
        return response
      }
      // Full login has already published its new owner under the cookie lease.
      // completeLogin checks that result's owner instead of the retired request owner.
      completeLogin(response)
      return response
    } catch (error) {
      if (isCurrentAdminSession(owner)) dispatch({ type: 'LOGIN_FAILURE' })
      throw error
    }
  }

  const refreshUser = async () => {
    const owner = captureAdminSession()
    if (!readAdminToken()) return
    try {
      const user = await authService.getCurrentUser()
      if (!mountedRef.current || !isCurrentAdminSession(owner)) return
      const token = readAdminToken()
      if (!isAdminTokenValid(token)) return
      dispatch({ type: 'SET_USER', payload: { user, token } })
    } catch (error) {
      if (!mountedRef.current || !isCurrentAdminSession(owner)) return
      if (isAuthenticationFailure(error)) logout('refresh-user-failed', owner)
      else dispatch({ type: 'SET_TOKEN_REFRESH_STATE',
        payload: { isRefreshing: false, error: 'Profile refresh failed' } })
    }
  }

  const restoreAuthentication = useCallback(async () => {
    const run = ++initializationRef.current
    const owner: AdminSessionOwner = captureAdminSession()
    const isCurrent = () => mountedRef.current && run === initializationRef.current
      && isCurrentAdminSession(owner)
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      if (!readAdminToken() && !owner.sessionId) {
        dispatch({ type: 'LOGOUT' })
        return
      }
      if (!isAdminTokenValid(readAdminToken())) {
        if (!owner.sessionId) { logout('init-expired-token-no-session', owner); return }
        await refreshToken()
      }
      if (!isCurrent()) return
      if (!isAdminTokenValid(readAdminToken())) throw new Error('Session recovery incomplete')
      const user = await authService.getCurrentUser()
      if (!isCurrent()) return
      const token = readAdminToken()
      if (!isAdminTokenValid(token)) throw new Error('Access token expired during profile lookup')
      dispatch({ type: 'SET_USER', payload: { user, token } })
    } catch (error) {
      if (!isCurrent()) return
      if (isAuthenticationFailure(error)) logout('init-auth-failed', owner)
      else {
        const token = readAdminToken()
        dispatch({ type: 'RECOVERY_FAILED', payload: {
          token: isAdminTokenValid(token) ? token : null,
          error: 'Session verification unavailable',
        } })
      }
    } finally {
      if (isCurrent()) dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [logout, refreshToken])

  const checkPermission = (permission: string): boolean => {
    if (!state.user) {
      if (import.meta.env.DEV) {
        console.warn('[AuthContext] checkPermission called without user', permission)
      }
      return false
    }
    
    // Get the role identifier to check - use role_id if available, fallback to role_code
    const roleToCheckRaw =
      state.user.role_id ??
      (state.user as any).roleId ??
      state.user.role_code ??
      (state.user as any).roleCode ??
      ''

    const normalizedRole = roleToCheckRaw.trim().toLowerCase().replace(/-/g, '_')
    const normalizedRoleFlat = normalizedRole.replace(/_/g, '')
    
    // Superadmin has all permissions (check both formats)
    if (
      normalizedRole === 'arl_superadmin' ||
      normalizedRole === 'superadmin' ||
      normalizedRoleFlat === 'arlsuperadmin' ||
      normalizedRoleFlat === 'superadmin'
    ) {
      return true
    }
    
    // Map role_id to permissions (supporting both role_id and role_code formats)
    const rolePermissions: Record<string, string[]> = {
      'arl_admin': [
        'users:read',
        'admin-users:read',
        'merchants:*',
        'orders:*',
        'system:read',
        'system:config',
        'dashboard:read',
        'audit:read',
        'monitor:read',
        'monitoring:read',
        'monitor:trigger',
        'monitoring:*',
        'economy:gas_refresh',
        'cache:read',
        'cache:write',
        'cache:config',
        'cooldown:read',
        'dlq:read',
        'dlq:reprocess',
        'dlq:resolve',
        'contracts:view',
        'contracts:manage',
        'contracts:deploy',
        'settlements:read',
        'settlements:trigger',
      ],
      'arl_operator': [
        'merchants:read',
        'merchants:update',
        'orders:*',
        'monitor:read',
        'monitoring:read',
        'monitor:trigger',
        'settlements:read',
        'settlements:trigger',
      ],
      'arl_support': [
        'merchants:read',
        'orders:read',
        'monitor:read',
        'monitoring:read',
      ],
      'arl_viewer': ['*:read'],
      'admin': [
        'users:read',
        'admin-users:read',
        'merchants:*',
        'orders:*',
        'system:read',
        'system:config',
        'dashboard:read',
        'audit:read',
        'monitor:read',
        'monitoring:read',
        'monitor:trigger',
        'monitoring:*',
        'economy:gas_refresh',
        'cache:read',
        'cache:write',
        'cache:config',
        'cooldown:read',
        'dlq:read',
        'dlq:reprocess',
        'dlq:resolve',
        'contracts:view',
        'contracts:manage',
        'contracts:deploy',
        'settlements:read',
        'settlements:trigger',
      ],
      'operator': [
        'merchants:read',
        'merchants:update',
        'orders:*',
        'monitor:read',
        'monitoring:read',
        'monitor:trigger',
        'settlements:read',
        'settlements:trigger',
      ],
      'support': [
        'merchants:read',
        'orders:read',
        'monitor:read',
        'monitoring:read',
      ],
      'viewer': ['*:read'],
    }
    
    const userPermissions =
      rolePermissions[normalizedRole] ||
      rolePermissions[normalizedRoleFlat] ||
      rolePermissions[normalizedRole.replace(/_/g, '')] ||
      []
    
    const normalizedPermission = permission.trim().toLowerCase()
    
    // Check for wildcard permissions
    if (userPermissions.includes('*')) return true
    if (userPermissions.includes(normalizedPermission)) return true
    
    // Check for resource:action pattern
    const [resource, action] = normalizedPermission.split(':')
    if (resource && userPermissions.includes(`${resource}:*`)) return true
    if (action && userPermissions.includes(`*:${action}`)) return true
    
    return false
  }

  useEffect(() => {
    mountedRef.current = true
    let observedOwner = captureAdminSession()
    const unsubscribe = subscribeAdminSession(() => {
      observedOwner = captureAdminSession()
      const token = readAdminToken()
      if (!token) dispatch({ type: 'LOGOUT' })
      else if (isAdminTokenValid(token)) dispatch({ type: 'REFRESH_TOKEN', payload: token })
    })
    let navigating = false
    const onStorage = (event: StorageEvent) => {
      if (event.key && !['admin_portal_token', 'admin_portal_session_id',
        'admin_portal_auth_transition'].includes(event.key)) return
      if (!isCurrentAdminSession(observedOwner)) {
        // A full navigation drops the previous user's page/query state as well.
        if (!navigating) { navigating = true; window.location.href = '/login' }
      } else {
        const token = readAdminToken()
        if (isAdminTokenValid(token)) dispatch({ type: 'REFRESH_TOKEN', payload: token })
      }
    }
    window.addEventListener('storage', onStorage)
    void restoreAuthentication()
    return () => {
      mountedRef.current = false
      initializationRef.current += 1
      unsubscribe()
      window.removeEventListener('storage', onStorage)
    }
  }, [restoreAuthentication])

  useEffect(() => {
    if (!state.isAuthenticated || !state.token) return
    const refresh = () => { void refreshToken().catch(() => undefined) }
    refresh()
    const interval = setInterval(refresh, 300000)
    const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [state.isAuthenticated, state.token, refreshToken])

  const value: AuthContextType = {
    ...state,
    login,
    completeLogin,
    logout,
    refreshUser,
    checkPermission,
    isTokenRefreshing: state.isTokenRefreshing || false,
    tokenRefreshError: state.tokenRefreshError || null,
  }
  
  if (import.meta.env.DEV) {
    (window as any).__adminAuthDebug = {
      user: state.user,
      token: state.token,
      isAuthenticated: state.isAuthenticated,
      isLoading: state.isLoading,
      updatedAt: new Date().toISOString(),
    }
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
